import { useMutation } from "@apollo/client";
import { Button, ButtonGroup, Card } from "@blueprintjs/core";
import { useState } from "react";
import { Link } from "react-router-dom";
import { CreateUserInput } from "../../../api/extra_schema";
import { User } from "../../../api/schema";
import {
  FlexContainer,
  FlexGrow,
  FlexRowStretch,
} from "../../../components/flex/Flex";
import {
  DeleteModal,
  DeleteStateHelper,
} from "../../../components/form/DeleteModal";
import { ModalInputType } from "../../../components/form/Form";
import {
  InputFormMode,
  ValuesVariables,
} from "../../../components/form/InputForm";
import { InputModal } from "../../../components/form/InputModal";
import { Table, TableColumns } from "../../../components/Table";
import { UserLanguage } from "../../../lang/lang";
import { useApiUsers } from "../../../store/Users";
import {
  CreateUserMutation,
  DeleteUserMutation,
  IDeleteUserMutation,
  RegisterResponse,
} from "../graphql";

export const UserListViewTableColumns: (
  delete_user: (id: number) => void
) => TableColumns<User> = (delete_user) => ({
  id: { label: UserLanguage().data.id },
  username: { label: UserLanguage().data.user.username },
  displayname: {
    label: UserLanguage().data.user.displayname,
  },
  email: {
    label: UserLanguage().data.user.email,
  },
  actions: {
    label: UserLanguage().actions.label,
    render: (item) => (
      <ButtonGroup>
        <Link to={`/user/${item.id}`}>
          <Button intent="primary" rightIcon="chevron-right">
            {UserLanguage().actions.show}
          </Button>
        </Link>
        <Button
          intent="danger"
          rightIcon="delete"
          onClick={() => delete_user(item.id)}
        >
          {UserLanguage().actions.delete}
        </Button>
      </ButtonGroup>
    ),
  },
});

export const UserListView = (props: {}) => {
  const { users, fetch_users } = useApiUsers();

  const [create_modal_open, set_create_modal_open] = useState(false);
  const [delete_user_mutation] = useMutation<
    IDeleteUserMutation,
    { id: number }
  >(DeleteUserMutation);

  const [delete_modal_state, set_delete_modal_state] =
    useState<DeleteStateHelper>({
      open: false,
      id: undefined,
      prettyPrint: undefined,
    });

  const [create_user] = useMutation<
    RegisterResponse,
    ValuesVariables<CreateUserInput>
  >(CreateUserMutation);

  console.log("users", users);

  const delete_user = (id: number) =>
    set_delete_modal_state({ open: true, id });

  return (
    <FlexContainer>
      <Card elevation={3} style={{ width: 900 }}>
        <FlexRowStretch>
          <div>
            <h2>{UserLanguage().pages.userlist.label}</h2>
          </div>
          <FlexGrow />
          <div>
            <Button
              icon="add"
              intent="success"
              onClick={() => set_create_modal_open(true)}
            >
              {UserLanguage().actions.create}
            </Button>
          </div>
        </FlexRowStretch>
        <InputModal<CreateUserInput, RegisterResponse>
          keys={{
            username: {
              type: ModalInputType.String,
              label: UserLanguage().data.user.username,
            },
            password: {
              type: ModalInputType.String,
              label: UserLanguage().data.user.password,
              is_password: true,
            },
            email: {
              type: ModalInputType.String,
              label: UserLanguage().data.user.email,
            },
          }}
          mode={InputFormMode.Create}
          mutation={create_user}
          open={create_modal_open}
          onClose={() => set_create_modal_open(false)}
          onSuccess={() => {
            set_create_modal_open(false);
            fetch_users();
          }}
          texts={UserLanguage().pages.userlist.actions}
        />
        <DeleteModal<IDeleteUserMutation>
          id={delete_modal_state.id}
          open={delete_modal_state.open}
          mutation={(id) => delete_user_mutation({ variables: { id } })}
          texts={UserLanguage().pages.userlist.actions}
          resetOnClose
          onClose={() => {
            set_delete_modal_state({ open: false, id: undefined });
          }}
          onSuccess={() => {
            set_delete_modal_state({ open: false, id: undefined });
            fetch_users();
          }}
          type={UserLanguage().data.user.label}
          prettyPrint={delete_modal_state.prettyPrint}
        />
        <Table<User>
          columns={UserListViewTableColumns(delete_user)}
          items={users}
        />
      </Card>
    </FlexContainer>
  );
};
